import { useState } from 'react';
import {
  updateWasserijItem,
  type WasserijItemDetail,
  type WasserijItemPatch,
} from '../../lib/wasserij-items';
import {
  ROLL_WIDTHS,
  calculateAutoCutPlan,
  cutPlanFromRolls,
  parseSnijplanOverride,
  formatSnijplanOverride,
  defaultRolls,
  clampRoll,
  marginForFulfillment,
} from '../../lib/snijplan';

// Kleda Base ondertapijt-SKU's die op maat geknipt worden: WAS-UPSELL-KLEDA-BASE
// (booking-upsell / client-app / order-detail) én SHS-ONDER-KLEDA-BASE (webshop-
// checkout + Nalevering). Beide krijgen de snijplan-editor — gespiegeld aan het
// wasserij-weergavefilter in KledaFieldAppRest.getUnderlayItems (ONDERTAPIJT_KNIP_CODES).
const ONDERTAPIJT_PRODUCT_CODES = ['WAS-UPSELL-KLEDA-BASE', 'SHS-ONDER-KLEDA-BASE'];

interface Props {
  item: WasserijItemDetail;
  /** Aangeroepen na een geslaagde mutatie → parent reload + notify. */
  onSaved: () => void;
  /** Compactere padding voor de smalle drawer-context. */
  dense?: boolean;
}

/**
 * Snijplan-editor voor een ondertapijt-WI (WAS-UPSELL-KLEDA-BASE). Gedeeld door
 * de order-drawer (WasserijItemDetailView) en het volledige scherm (ItemDetailView).
 * Zelf-verbergend: rendert null voor niet-ondertapijt items.
 */
export function SnijplanSection({ item, onSaved, dense }: Props) {
  const [editSnijplan, setEditSnijplan] = useState(false);
  const [snijplanMode, setSnijplanMode] = useState<'auto' | 'manual'>('auto');
  const [snijplanRolls, setSnijplanRolls] = useState<number[]>([]);
  const [snijplanNotitie, setSnijplanNotitie] = useState('');
  const [snijplanSaving, setSnijplanSaving] = useState(false);
  const [snijplanError, setSnijplanError] = useState<string | null>(null);

  const isOndertapijt = ONDERTAPIJT_PRODUCT_CODES.includes(item.product.code);
  const isRound = item.vorm === 'Rond' || (item.diameterCm != null && item.diameterCm > 0);
  // Webshop-orders (Eigen verzending) worden op exacte maat gemaakt: geen marge.
  const margin = marginForFulfillment(item.fulfillmentType);
  const isExact = margin === 0;
  const autoPlan = isOndertapijt
    ? calculateAutoCutPlan(item.lengteCm, item.breedteCm, isRound, item.diameterCm, margin)
    : null;
  const overrideRolls = parseSnijplanOverride(item.ondertapijtSnijplan);
  const effectivePlan = overrideRolls
    ? cutPlanFromRolls(overrideRolls, item.lengteCm, item.breedteCm, isRound, item.diameterCm, margin)
    : autoPlan;
  const previewPlan =
    editSnijplan && snijplanMode === 'manual'
      ? cutPlanFromRolls(snijplanRolls, item.lengteCm, item.breedteCm, isRound, item.diameterCm, margin)
      : null;

  if (!isOndertapijt) return null;

  const startEditSnijplan = () => {
    const ov = parseSnijplanOverride(item.ondertapijtSnijplan);
    if (ov) {
      setSnijplanMode('manual');
      setSnijplanRolls(ov);
    } else {
      setSnijplanMode('auto');
      setSnijplanRolls(autoPlan ? defaultRolls(autoPlan.pieces, autoPlan.underlayWidth) : [clampRoll(120)]);
    }
    setSnijplanNotitie(item.ondertapijtSnijplanNotitie || '');
    setSnijplanError(null);
    setEditSnijplan(true);
  };

  const cancelEditSnijplan = () => {
    setEditSnijplan(false);
    setSnijplanError(null);
  };

  const setSnijplanPieces = (n: number) => {
    const uw = autoPlan?.underlayWidth ?? (snijplanRolls.reduce((s, r) => s + r, 0) || 240);
    setSnijplanRolls(defaultRolls(n, uw));
  };

  const setSnijplanRollAt = (idx: number, value: number) => {
    setSnijplanRolls((prev) => prev.map((r, i) => (i === idx ? value : r)));
  };

  const saveSnijplan = async () => {
    setSnijplanSaving(true);
    setSnijplanError(null);
    try {
      const patch: WasserijItemPatch = {
        Ondertapijt_Snijplan__c: snijplanMode === 'manual' ? formatSnijplanOverride(snijplanRolls) : null,
        Ondertapijt_Snijplan_Notitie__c: snijplanNotitie.trim() || null,
      };
      await updateWasserijItem(item.id, patch);
      setEditSnijplan(false);
      onSaved();
    } catch (err) {
      setSnijplanError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSnijplanSaving(false);
    }
  };

  return (
    <section className={`bg-white rounded-lg border border-slate-200 ${dense ? 'p-3' : 'p-4'} space-y-2`}>
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Snijplan</h3>
        {!editSnijplan && (
          <button
            type="button"
            onClick={startEditSnijplan}
            className="text-xs text-indigo-600 hover:underline"
          >
            Aanpassen
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-400 -mt-1">
        Wat de wasserij-medewerker in de field-app ziet.{' '}
        {isExact
          ? 'Webshop-order: exacte bestelde maat, geen marge.'
          : 'Snijlengte is altijd kleed − 10 cm.'}
      </p>

      {!editSnijplan ? (
        <div className="space-y-2">
          {effectivePlan ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-900">
                  {isRound
                    ? `⌀ ${effectivePlan.underlayWidth} cm`
                    : `${effectivePlan.underlayLength} × ${effectivePlan.underlayWidth} cm`}
                </span>
                {effectivePlan.isManual ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                    handmatig
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                    automatisch
                  </span>
                )}
                {effectivePlan.pieces === 2 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200">
                    2-delig
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-600 space-y-0.5">
                {effectivePlan.rolls.map((r, i) => (
                  <div key={i}>
                    Rol {r} cm → snij {effectivePlan.cutLength} cm
                  </div>
                ))}
                {effectivePlan.pieces > 1 && (
                  <div className="text-slate-400">
                    {effectivePlan.rolls.join(' + ')} = {effectivePlan.underlayWidth} cm
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Maten ontbreken — snijplan niet te berekenen.</p>
          )}
          {item.ondertapijtSnijplanNotitie && (
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2 text-xs text-indigo-800">
              <span className="font-medium">📝 Notitie voor knipper:</span>{' '}
              {item.ondertapijtSnijplanNotitie}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-1.5 text-slate-700">
              <input
                type="radio"
                name={`snijplanMode-${item.id}`}
                checked={snijplanMode === 'auto'}
                onChange={() => setSnijplanMode('auto')}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              Automatisch
            </label>
            <label className="flex items-center gap-1.5 text-slate-700">
              <input
                type="radio"
                name={`snijplanMode-${item.id}`}
                checked={snijplanMode === 'manual'}
                onChange={() => {
                  setSnijplanMode('manual');
                  if (snijplanRolls.length === 0) setSnijplanPieces(1);
                }}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              Handmatig
            </label>
          </div>

          {snijplanMode === 'auto' ? (
            <p className="text-xs text-slate-500">
              {autoPlan
                ? `${autoPlan.pieces} ${autoPlan.pieces === 1 ? 'stuk' : 'stukken'} — ${autoPlan.rolls.join(' + ')} cm (${autoPlan.underlayLength} × ${autoPlan.underlayWidth} cm)`
                : 'Maten ontbreken.'}
            </p>
          ) : (
            <div className="space-y-2">
              <Row label="Aantal stukken">
                <select
                  value={snijplanRolls.length || 1}
                  onChange={(e) => setSnijplanPieces(Number(e.target.value))}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                >
                  <option value={1}>1 stuk</option>
                  <option value={2}>2 stukken</option>
                </select>
              </Row>
              {snijplanRolls.map((r, i) => (
                <Row key={i} label={snijplanRolls.length > 1 ? `Rolbreedte ${i + 1}` : 'Rolbreedte'}>
                  <select
                    value={r}
                    onChange={(e) => setSnijplanRollAt(i, Number(e.target.value))}
                    className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  >
                    {ROLL_WIDTHS.map((w) => (
                      <option key={w} value={w}>
                        {w} cm
                      </option>
                    ))}
                  </select>
                </Row>
              ))}
              {previewPlan ? (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                  <div className="font-medium">
                    {previewPlan.underlayLength} × {previewPlan.underlayWidth} cm
                  </div>
                  {previewPlan.rolls.map((rr, ix) => (
                    <div key={ix}>
                      Rol {rr} cm → snij {previewPlan.cutLength} cm
                    </div>
                  ))}
                  <div className="mt-1 text-amber-600">✓ zo getoond aan de wasserij-medewerker</div>
                </div>
              ) : (
                <p className="text-xs text-slate-400">Maten ontbreken voor preview.</p>
              )}
            </div>
          )}

          <div className="pt-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">Notitie voor knipper</label>
            <textarea
              value={snijplanNotitie}
              onChange={(e) => setSnijplanNotitie(e.target.value)}
              rows={2}
              maxLength={255}
              placeholder="Bijv. Klant akkoord dat het uit 2 stukken bestaat"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {snijplanError && <p className="text-xs text-red-600">{snijplanError}</p>}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={saveSnijplan}
              disabled={snijplanSaving}
              className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {snijplanSaving ? 'Opslaan…' : 'Opslaan'}
            </button>
            <button
              type="button"
              onClick={cancelEditSnijplan}
              disabled={snijplanSaving}
              className="text-xs text-slate-600 hover:text-slate-900"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-center">
      <label className="text-xs text-slate-500 col-span-1">{label}</label>
      <div className="col-span-2">{children}</div>
    </div>
  );
}
